import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import * as _ from 'lodash';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { isNullOrUndefined } from '../../../models/utils';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { DragDropData } from '../../../models/drag-drop-data';
import { HelpIndex } from '../../../models/enums';
import { GenericColDef, GridContext, FilterTemplateType } from '../../../models/grid-context';
import { GroupField, GroupFieldDefinition, GroupFieldMode } from '../../../models/group-field';
import { CommonService } from '../../../services/common/common.service';
import { DragDropCellComponent } from '../../grid-templates/drag-drop-cell/drag-drop-cell.component';
import { LeftRightSelectionBoxComponent } from '../../left-right-selection-box/left-right-selection-box.component';
import { DrObject } from '../../../models/dr-object';
import { SelectionChangedEvent } from 'ag-grid-community';

export interface CreateEditGroupFieldModalData {
    availableFields: Array<DragDropData>;
    groupField: GroupField;
}

@Component({
    standalone: false,
    selector: 'res-create-edit-group-field-modal',
    templateUrl: './create-edit-group-field-modal.component.html',
    styleUrls: ['./create-edit-group-field-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class CreateEditGroupFieldModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild(LeftRightSelectionBoxComponent, { static: true }) leftRightSelectionBoxComponent: LeftRightSelectionBoxComponent;

    modalTitle: string;
    modalClass = 'create-edit-group-field';
    modalAutomationId = 'group-field';
    modalHelpIndex = HelpIndex.add_edit_export_group_field_dlg;

    itemsLeft: Array<DragDropData> = new Array<DragDropData>();
    itemsRight: Array<DragDropData> = new Array<DragDropData>();

    originalFieldsMap: Map<string, string>; // name, removed (true,false)
    currentFieldsMap: Map<string, boolean>; // name, second value doesn't matter so always set to true
    filterQuery = '';
    filteredList = [];


    leftListColumns = new Array<GenericColDef>(
        {
            headerName: 'Field Name', field: 'name', sortBy: 'name', automationId: this.modalAutomationId,
            cellRenderer: DragDropCellComponent,
            cellRendererParams: {
                showDragDots: true,
                dragScope: (item: DragDropData) => ['availableField'],
                dropScope: (item: DragDropData) => ['field'],
                onDrop: (dragData: DragDropData, dropData: DragDropData) => this.leftRightSelectionBoxComponent.onDropLeft(dragData, dropData, null, this.filterQuery)
            },
            lockVisible: true,
            filterType: FilterTemplateType.freeform,
        }
    );
    rightListColumns = new Array<GenericColDef>(
        {
            headerName: 'Field Name', field: 'name', sortBy: 'name', automationId: this.modalAutomationId,
            cellRenderer: DragDropCellComponent,
            cellRendererParams: {
                showDragDots: true,
                dragScope: (item: DragDropData) => ['field'],
                dropScope: (item: DragDropData) => ['field', 'availableField'],
                onDrop: (dragData: DragDropData, dropData: DragDropData) => this.leftRightSelectionBoxComponent.onDropRight(dragData, dropData, this.filterQuery, this.mode)
            },
            lockVisible: true
        }
    );

    leftListGridContext = new GridContext({
        sortBy: 'name', sortAscending: true, sortEnabled: false,
        inlineGridFilter: true,
        onFilterApply: (queryStr: string) => this.onFilterApply(queryStr),
    });

    readonly GroupFieldMode = GroupFieldMode;
    isDirty: boolean;
    name: string;
    originalName: string;
    mode = GroupFieldMode.FIRST_NON_EMPTY;
    originalMode: GroupFieldMode;
    modeValue: string;
    originalModeValue: string;
    propagateToFamily: boolean;
    originalPropagateToFamily: boolean;

    constructor(
        private modalService: BsModalService,
        private modalRef: BsModalRef,
        private commonService: CommonService) {
        super(modalService, modalRef);
    }

    ngOnInit() {
        this.originalFieldsMap = new Map<string, string>();
        this.currentFieldsMap = new Map<string, boolean>();
        const createEditGroupFieldModalData = this.modalData as CreateEditGroupFieldModalData;
        if (createEditGroupFieldModalData && createEditGroupFieldModalData.availableFields) {
            this.itemsLeft = this.leftRightSelectionBoxComponent.initialItemLeftList = createEditGroupFieldModalData.availableFields;
            if (createEditGroupFieldModalData.groupField && createEditGroupFieldModalData.groupField.externalName) {
                // edit group field
                this.modalTitle = 'Edit Transformed Field';
                this.name = createEditGroupFieldModalData.groupField.externalName.trim();
                this.originalName = this.name;
                if (createEditGroupFieldModalData.groupField.groupFieldDefinition) {
                    if (<GroupFieldMode>createEditGroupFieldModalData.groupField.groupFieldDefinition.mode) {
                        this.mode = this.originalMode = createEditGroupFieldModalData.groupField.groupFieldDefinition.mode;
                    }
                    this.modeValue = createEditGroupFieldModalData.groupField.groupFieldDefinition.mode === GroupFieldMode.FIRST_NON_EMPTY ?
                        createEditGroupFieldModalData.groupField.groupFieldDefinition.defaultValue : createEditGroupFieldModalData.groupField.groupFieldDefinition.delimiter;
                    if (!isNullOrUndefined(this.modeValue)) {
                        this.modeValue = this.originalModeValue = this.modeValue.trim();
                    } else {
                        this.modeValue = this.originalModeValue = null;
                    }
                    this.propagateToFamily = this.originalPropagateToFamily = createEditGroupFieldModalData.groupField.groupFieldDefinition.propagateToChildren;
                }
                if (createEditGroupFieldModalData.groupField.fields) {
                    this.itemsRight = createEditGroupFieldModalData.groupField.fields;
                    this.itemsRight.forEach((element, index) => {
                        this.originalFieldsMap.set(element.name, 'false');
                    });
                }
            } else {
                // new group field
                this.modalTitle = 'Create Transformed Field';
            }
        }
        setTimeout(() => {
            this.leftRightSelectionBoxComponent.leftListIsSingleSelection = this.mode === GroupFieldMode.DUPLICATE_VALUES;
            this.leftRightSelectionBoxComponent.setLeftItems(this.itemsLeft);
            this.leftRightSelectionBoxComponent.setRightItems(this.itemsRight);
        });
    }

    modeChanged(val: GroupFieldMode) {
        this.mode = val;
        this.modeValue = this.mode === GroupFieldMode.FIRST_NON_EMPTY ? '' : ';';
        this.leftRightSelectionBoxComponent.leftListIsSingleSelection = this.mode === GroupFieldMode.DUPLICATE_VALUES;
        if (this.mode === GroupFieldMode.DUPLICATE_VALUES) {
            if (this.itemsRight.length > 1) {
                const items = this.itemsRight.splice(1);
                this.itemsLeft.push(...items);
                this.leftRightSelectionBoxComponent.setLeftItems(this.itemsLeft);
            }
            this.leftRightSelectionBoxComponent.checkSelectedNodesLeft();
        }
        this.leftRightSelectionBoxComponent.setRightItems(this.itemsRight);
    }

    isNameDirty(): boolean {
        if (!isNullOrUndefined(this.name)) {
            if (this.name.trim() !== this.originalName.trim()) {
                return true;
            }
        }
        return false;
    }

    dirty(): boolean {
        if (this.mode !== this.originalMode) {
            return true;
        }

        if (this.originalModeValue !== this.modeValue) {
            return true;
        }

        if (this.originalPropagateToFamily !== this.propagateToFamily) {
            return true;
        }

        if (this.isNameDirty()) {
            return true;
        }

        if (this.modalTitle === 'Edit Transformed Field' && this.isDirty) {
            return true;
        }

        return false;
    }

    customMoveItems = (data: any): void => {
        if (data && data.mode && data.items && data.items.length > 0) {
            if (data.mode === 'add') {
                if (this.mode === GroupFieldMode.DUPLICATE_VALUES && data.items.length >= 1) {
                    this.leftRightSelectionBoxComponent.replaceItem(data.items[0], this.itemsLeft, this.itemsRight);
                } else {
                    this.leftRightSelectionBoxComponent.moveDraggedItems(data.items, this.itemsLeft, this.itemsRight, -1);
                }

                // update currentFieldsMap
                data.items.forEach((entry, index) => {
                    const originalItem = this.originalFieldsMap.get(entry.name);
                    if (isNullOrUndefined(originalItem)) {
                        const currentItem = this.currentFieldsMap.get(entry.name);
                        if ((data.mode === 'remove') && !isNullOrUndefined(currentItem)) {
                            this.currentFieldsMap.delete(entry.name);
                        } else if ((data.mode === 'add') && isNullOrUndefined(currentItem)) {
                            this.currentFieldsMap.set(entry.name, true); // the value doesn't matter
                        }
                    }
                });
            } else {
                this.leftRightSelectionBoxComponent.moveDraggedItems(data.items, this.itemsRight, this.itemsLeft, -1);
            }
        }
        this.onFilterApply(this.filterQuery);
        this.isDirty = this.areItemsDirty(data.items, data.mode);
    }

    afterDropUpdate(dropped): void {
        // update currentFieldsMap
        if (!isNullOrUndefined(dropped.items)) {
            dropped.items.forEach((entry) => {
                const currentItem = this.currentFieldsMap.get(entry.name);
                if (dropped.type === 'left' && !isNullOrUndefined(currentItem)) {
                    this.currentFieldsMap.delete(entry.name);
                } else if (dropped.type === 'right' && isNullOrUndefined(currentItem)) {
                    this.currentFieldsMap.set(entry.name, true); // the value doesn't matter
                }
            });
        }

        this.areItemsDirty(dropped.items, dropped.type === 'left' ? 'remove' : 'add');
        this.onFilterApply(this.filterQuery);
    }

    areRequiredFieldsComplete(): boolean {
        return !DrObject.isEmpty(this.name) && this.itemsRight && (this.itemsRight.length > 0);
    }

    okEnabled(): boolean {
        let isEnabled = false;
        if (this.areRequiredFieldsComplete()) {
            if (this.dirty() || this.isDirty) {
                isEnabled = true;
            }
        }
        return isEnabled;
    }

    ok(): void {
        const group = new GroupField({ name: this.name });
        group.fields = this.itemsRight;
        if (!isNullOrUndefined(this.modeValue)) {
            this.modeValue = this.modeValue.trim();
        }
        group.groupFieldDefinition = new GroupFieldDefinition({
            mode: this.mode,
            defaultValue: this.mode === GroupFieldMode.FIRST_NON_EMPTY ? this.modeValue : undefined,
            delimiter: this.mode !== GroupFieldMode.FIRST_NON_EMPTY ? this.modeValue : undefined,
            propagateToChildren: this.propagateToFamily
        });
        this.closeModal(true, group);
    }

    areItemsDirty(items, mode): boolean {
        let bDirty = false;
        if (items && (items.length > 0)) {
            items.forEach((entry, index) => {
                if (this.originalFieldsMap.has(entry.name)) {
                    const removed = this.originalFieldsMap.get(entry.name);
                    if (!isNullOrUndefined(removed)) {
                        if ((removed === 'false') && (mode === 'remove')) {
                            this.originalFieldsMap.set(entry.name, 'true');
                        } else if ((removed === 'true') && (mode === 'add')) {
                            this.originalFieldsMap.set(entry.name, 'false');
                        }
                    }
                }
            });
        }

        const mapValues = Array.from(this.originalFieldsMap.values());
        const anyItemsRemoved = mapValues.join(',').includes('true');
        if (anyItemsRemoved) {
            bDirty = true;
        } else if (this.currentFieldsMap.size > 0) {
            bDirty = true;
        }
        this.isDirty = bDirty;
        return bDirty;
    }

    CustomFilterCallback = (event?: SelectionChangedEvent) => {
        this.leftRightSelectionBoxComponent.setLeftItems(this.itemsLeft);
    }

    onFilterApply(queryStr: string): void {
        this.filterQuery = queryStr;
        if (!queryStr || queryStr === '') {
            this.filteredList = this.itemsLeft;
        } else {
            this.filteredList = _.filter(this.itemsLeft, (field) => field.name ? field.name.toLowerCase().indexOf(queryStr.toLowerCase()) !== -1 : false);
        }
        this.leftRightSelectionBoxComponent.setLeftItems(this.filteredList);
    }
}
